import { v4 as uuid } from 'uuid';
import { logger } from './logger';
import { assertRoomID, isParticipantID, isRoomID } from './typeGuards';
import { MAIN_ROOM_ID } from './constants';
import { normalizeConferenceState, normalizeParticipant, normalizePresentationEvent, } from './utils';
export const createEventHandler = (deps) => {
    const { signals, eventSignals, callSignals, props, participantSync, presentationCache, } = deps;
    const getSpeakers = (roomId, sources) => {
        if (!isRoomID(roomId)) {
            return [];
        }
        return (sources?.flatMap(source => {
            if (!isParticipantID(source.participant_uuid)) {
                return [];
            }
            const participant = deps
                .getParticipants()
                .get(roomId, source.participant_uuid);
            const displayName = participant?.displayName;
            return displayName
                ? [{ participantUuid: source.participant_uuid, displayName }]
                : [];
        }) ?? []);
    };
    const isMe = (roomId, participantUuid) => participantUuid ===
        (roomId === MAIN_ROOM_ID
            ? props.participantUuid
            : deps.getBreakoutParticipantUuid(roomId));
    const handleParticipantsSyncEnded = id => {
        logger.debug({ roomID: id }, '[Participant] Sync End');
        signals.onParticipants.emit({
            id,
            participants: deps.getParticipants().getAll(id),
        });
    };
    const handleParticipantCreate = (id, participant) => {
        logger.debug({ roomID: id, participant }, '[Participant] Create');
        const isCurrentParticipant = isMe(id, participant.uuid);
        if (isCurrentParticipant && participant.serviceType) {
            deps.setServiceType(participant.serviceType);
        }
        deps.getParticipants().set(id, participant);
        // Only emit onParticipantJoined Signal when it is NOT doing
        // `participant_sync`
        if (!participantSync.get(id)) {
            signals.onParticipantJoined.emit({ id, participant });
        }
        /*
         * We would like the following code to only be executed when the current participant first joins.
         * We were assuming that if the current participant was created outside the participant sync events that
         * it was when they first joined but Greg explained that this is not always true:
         * "If we get a roster tick which has the new user before we get the eventsource and start sending
         * the data then the new user will be part of the "current roster" data and hence be inside the
         * sync begin/end. If the roster tick which includes the new user happens later,
         * it wont be in the current roster and will come along later outside the sync messages."
         *
         * Therefore we need to execute this code even if current participant participant_create event was part of
         * sync or not.
         */
        if (isCurrentParticipant) {
            signals.onMe.emit({ id, participant });
            if (participant.canMute) {
                signals.onMyselfMuted.emit({
                    isServerMuted: participant.isMuted,
                    hasServerMuteStateChanged: false,
                });
            }
        }
    };
    const handleParticipantUpdate = (id, participant) => {
        // Skip when there is no such participant
        const existingParticipant = deps
            .getParticipants()
            .get(id, participant.uuid);
        if (!existingParticipant) {
            return;
        }
        logger.debug({ roomID: id, participant }, '[Participant] Update');
        const isCurrentParticipant = isMe(id, participant.uuid);
        if (isCurrentParticipant) {
            const muteChanged = participant.isMuted !==
                deps.getParticipants().get(id, participant.uuid)?.isMuted;
            if (participant.canMute && muteChanged) {
                signals.onMyselfMuted.emit({
                    isServerMuted: participant.isMuted,
                    hasServerMuteStateChanged: true,
                });
            }
            if (participant.serviceType) {
                deps.setServiceType(participant.serviceType);
            }
        }
        else if (participant.raisedHand !== existingParticipant.raisedHand) {
            signals.onRaiseHand.emit({ id, participant });
        }
        deps.getParticipants().set(id, participant);
        if (isCurrentParticipant) {
            signals.onMe.emit({ id, participant });
        }
        signals.onParticipantUpdated.emit({ id, participant });
    };
    const handleParticipantDelete = (id, participant) => {
        if (!deps.getParticipants().remove(id, participant.uuid)) {
            return;
        }
        logger.debug({ roomID: id, participant }, '[Participant] Delete');
        signals.onParticipantLeft.emit({
            id,
            participant,
        });
    };
    const handleOnMessage = (roomId, { type, uuid: userId, payload: message, direct, origin }) => {
        const meta = {
            at: new Date(),
            id: uuid(),
            roomId,
            displayName: (userId &&
                deps.getParticipants().get(MAIN_ROOM_ID, userId)
                    ?.displayName) ||
                origin,
            userId,
            direct,
        };
        const [payloadType] = (type ?? '').split(';');
        if (payloadType === 'text/plain') {
            signals.onMessage.emit({
                ...meta,
                message,
            });
        }
        else {
            try {
                signals.onApplicationMessage.emit({
                    ...meta,
                    message: JSON.parse(message),
                });
            }
            catch (error) {
                logger.error({ error }, 'Could not parse application message payload');
            }
        }
    };
    const handleConferenceStatus = (roomId, status) => {
        deps.getConferenceStatus().set(roomId, status);
        if (status.breakout) {
            deps.invalidateCache('breakoutRoomNames');
        }
        signals.onConferenceStatus.emit({ id: roomId, status });
    };
    const handleSyncBegin = (roomId) => {
        logger.debug({ roomID: roomId }, '[Participant] Sync Begin');
        participantSync.set(roomId, true);
        deps.getParticipants().set(roomId);
    };
    const handleSyncEnd = (roomId) => {
        participantSync.set(roomId, false);
        handleParticipantsSyncEnded(roomId);
    };
    const handleDelete = (roomId, uuid) => {
        if (!isParticipantID(uuid)) {
            return;
        }
        const participant = deps.getParticipants().get(roomId, uuid);
        if (participant) {
            handleParticipantDelete(roomId, participant);
        }
    };
    const subscribe = () => [
        eventSignals.onConnected.add(signals.onConnected.emit),
        eventSignals.onPresentationStart.add(event => {
            // With the MCU-side of limitations, we cannot use `presenter_uuid` from
            // the event to reliably correlate the event to the participant.
            // @see {@link https://github.com/pexip/mcu/issues/46379}
            const currentRoom = deps.getCurrentRoomId();
            if (currentRoom === MAIN_ROOM_ID) {
                void deps.getCall()?.receivePresentation(event);
            }
            presentationCache.set(currentRoom, event);
        }),
        eventSignals.onPresentationStop.add(() => {
            const currentRoom = deps.getCurrentRoomId();
            if (currentRoom === MAIN_ROOM_ID) {
                deps.getCall()?.stopReceivingPresentation();
            }
            presentationCache.delete(currentRoom);
        }),
        eventSignals.onCallDisconnected.add(({ call_uuid: callUuid }) => {
            logger.debug({ callUuid, currentCallUuid: deps.getCallUuid() }, 'onCallDisconnected');
            deps.teardownCall();
            signals.onCallDisconnected.emit({ call_uuid: callUuid });
        }),
        eventSignals.onBreakoutBegin.add(room => {
            logger.debug({ room }, '[Room] Breakout Begin');
            deps.getBreakoutRooms().set(room.breakout_uuid, room);
            deps.invalidateCache('rooms');
            signals.onBreakoutBegin.emit(room);
        }),
        eventSignals.onBreakoutEvent.add(event => {
            assertRoomID(event.breakout_uuid);
            switch (event.event) {
                case 'participant_sync_begin':
                    handleSyncBegin(event.breakout_uuid);
                    break;
                case 'participant_create':
                    handleParticipantCreate(event.breakout_uuid, normalizeParticipant(event.data));
                    break;
                case 'participant_update':
                    handleParticipantUpdate(event.breakout_uuid, normalizeParticipant(event.data));
                    break;
                case 'participant_delete':
                    handleDelete(event.breakout_uuid, event.data.uuid);
                    break;
                case 'participant_sync_end':
                    handleSyncEnd(event.breakout_uuid);
                    break;
                case 'message_received':
                    handleOnMessage(event.breakout_uuid, event.data);
                    break;
                case 'conference_update':
                    handleConferenceStatus(event.breakout_uuid, normalizeConferenceState(event.data));
                    break;
                case 'presentation_start': {
                    if (event.breakout_uuid === props.breakoutUuid) {
                        void deps
                            .getCall()
                            ?.receivePresentation(normalizePresentationEvent(event.data));
                    }
                    // Cache presentation_start event if presentation is started
                    presentationCache.set(event.breakout_uuid, normalizePresentationEvent(event.data));
                    break;
                }
                case 'presentation_stop': {
                    if (event.breakout_uuid === props.breakoutUuid) {
                        deps.getCall()?.stopReceivingPresentation();
                    }
                    // Clear cached presentation_start event if presentation is stopped
                    presentationCache.delete(event.breakout_uuid);
                    break;
                }
                case 'live_captions': {
                    if (event.breakout_uuid === props.breakoutUuid) {
                        signals.onLiveCaptions.emit({
                            data: event.data.data,
                            isFinal: event.data.is_final,
                            speakers: getSpeakers(event.breakout_uuid, event.data.sources),
                        });
                    }
                    break;
                }
            }
        }),
        eventSignals.onBreakoutEnd.add(room => {
            logger.debug({ room }, '[Room] Breakout End');
            deps.getBreakoutRooms().delete(room.breakout_uuid);
            deps.invalidateCache('rooms');
            deps.getConferenceStatus().delete(room.breakout_uuid);
            deps.getParticipants().remove(room.breakout_uuid);
            signals.onBreakoutEnd.emit(room);
        }),
        eventSignals.onParticipantCreate.add(participant => handleParticipantCreate(MAIN_ROOM_ID, participant)),
        eventSignals.onParticipantUpdate.add(participant => handleParticipantUpdate(MAIN_ROOM_ID, participant)),
        eventSignals.onParticipantDelete.add(uuid => handleDelete(MAIN_ROOM_ID, uuid)),
        eventSignals.onParticipantSyncBegin.add(() => handleSyncBegin(MAIN_ROOM_ID)),
        eventSignals.onParticipantSyncEnd.add(() => handleSyncEnd(MAIN_ROOM_ID)),
        eventSignals.onMessage.add(event => {
            handleOnMessage(MAIN_ROOM_ID, event);
        }),
        eventSignals.onLayoutUpdate.add(event => {
            signals.onRequestedLayout.emit({
                primaryScreen: {
                    hostLayout: event.requested_layout?.primary_screen.chair_layout,
                    guestLayout: event.requested_layout?.primary_screen.guest_layout,
                },
            });
            signals.onLayoutOverlayTextEnabled.emit(!!event.overlay_text_enabled);
            signals.onLayoutUpdate.emit(event);
        }),
        eventSignals.onStageUpdate.add(stages => signals.onStage.emit(stages.map(stage => ({
            userId: stage.participant_uuid,
            stageIndex: stage.stage_index,
            vad: stage.vad,
        })))),
        eventSignals.onDisconnect.add(({ reason }) => {
            deps.cleanupAndDisconnect(reason);
        }),
        eventSignals.onConferenceUpdate.add(status => {
            handleConferenceStatus(MAIN_ROOM_ID, status);
        }),
        eventSignals.onRefer.add(details => {
            logger.debug({ details }, '[Room] Refer');
            signals.onTransfer.emit({
                ...details,
                callTag: deps.getCurrentCallTag(),
                breakoutName: details.breakout_name,
                target: details.target,
            });
        }),
        eventSignals.onCancelRefer.add(details => {
            logger.debug({ details }, '[Room] Cancel Refer');
            signals.onCancelTransfer.emit(details);
        }),
        eventSignals.onLiveCaptions.add(captions => {
            signals.onLiveCaptions.emit({
                data: captions.data,
                isFinal: captions.is_final,
                speakers: getSpeakers(MAIN_ROOM_ID, captions.sources),
            });
        }),
        eventSignals.onSplashScreen.add(result => {
            if (!result || !('screen_key' in result)) {
                signals.onSplashScreen.emit();
                return;
            }
            const splashScreen = deps.getThemeSchema()?.[result.screen_key];
            if (splashScreen && deps.getRequestClientToken()) {
                signals.onSplashScreen.emit({
                    screenKey: result.screen_key,
                    text: splashScreen.elements[0]?.text ?? '',
                    background: `${props.host}/api/client/v2/conferences/${props.conferenceAlias}/theme/${splashScreen.background.path}?token=${encodeURIComponent(deps.getRequestClientToken() ?? '')}`,
                    displayDuration: result.display_duration * 1000,
                });
            }
        }),
        eventSignals.onNewOffer.add(({ sdp }) => {
            signals.onNewOffer.emit(sdp);
        }),
        eventSignals.onUpdateSdp.add(({ sdp }) => {
            signals.onUpdateSdp.emit(sdp);
        }),
        eventSignals.onNewCandidate.add(({ candidate, mid: sdpMid, ufrag: usernameFragment }) => {
            signals.onIceCandidate.emit(new RTCIceCandidate({ candidate, sdpMid, usernameFragment }));
        }),
        eventSignals.onPeerDisconnect.add(() => {
            callSignals.onSecureCheckCode.emit('');
            signals.onPeerDisconnect.emit();
        }),
        eventSignals.onRefreshToken.add(() => {
            deps.refreshToken();
        }),
        eventSignals.onBreakoutRefer.add(({ breakout_uuid, breakout_name, requester_uuid, ...rest }) => {
            logger.debug({
                details: {
                    ...rest,
                    breakoutUuid: breakout_uuid,
                    breakoutName: breakout_name,
                    requesterUuid: requester_uuid,
                },
            }, '[Room] Breakout Refer');
            assertRoomID(breakout_uuid);
            signals.onBreakoutRefer.emit({
                breakoutUuid: breakout_uuid,
                breakoutName: breakout_name,
                requesterUuid: requester_uuid,
            });
        }),
        eventSignals.onFecc.add(feccEvent => {
            signals.onFecc.emit(feccEvent);
        }),
    ];
    return {
        subscribe,
    };
};
//# sourceMappingURL=eventHandler.js.map